import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import Icon from '@mdi/react';
import { mdiMagnify } from '@mdi/js';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import {
    Row,
    Col,
    Nav,
    NavItem,
    NavLink,
    Button,
    Badge,
    Card,
    CardBody,
    Input,
    InputGroup,
    InputGroupAddon,
} from 'reactstrap';
import {
    loadIntegrationEvents,
    loadIntegrationEvent,
    replayWebhook,
    replayCallback,
} from '../../redux/modules/integration_events';
import { loadTransaction } from '../../redux/modules/transactions';
import { User } from '../../models';
import Role from '../../components/Role/Role';
import TransactionModal from '../../components/TransactionModal/TransactionModal';
import { Transactions } from '../../services';
import {
    integrationEventStatusBadgeColor,
    integrationEventKindLabel,
    integrationEventStatusLabel,
    formatIntegrationEventDateTime,
    parseMerchantCallbackName,
    urlWithoutQuery,
    integrationEventPrimaryTitle,
} from '../../utils/integrationEventLabels';
import './Workbench.css';

const formatJson = (value) => {
    try {
        if (value === null || value === undefined) {
            return '';
        }
        if (typeof value === 'string') {
            return value;
        }
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
};

/**
 * @param {import('../../models/Transaction').default|null} tx
 * @param {string} uuid
 */
const transactionDisplayLabel = (tx, uuid) => {
    if (!uuid) {
        return '';
    }
    if (tx && tx.id === uuid) {
        const orderId = tx.order_id != null ? String(tx.order_id).trim() : '';
        if (orderId) {
            return orderId;
        }
        const ref = tx.ref != null ? String(tx.ref).trim() : '';
        if (ref) {
            return `${ref} (${uuid})`;
        }
    }
    return uuid;
};

const buildEventListParams = ({
    search,
    resource_ref,
    from,
    to,
    kind,
    status,
    payment_request_id,
}) => {
    const params = {};
    if (kind) {
        params.kind = kind;
    } else {
        params.exclude_kind = 'rest_api';
    }
    if (status) {
        params.status = status;
    }
    if (search && search.trim()) {
        params.search = search.trim();
    }
    if (resource_ref && resource_ref.trim()) {
        params.resource_ref = resource_ref.trim();
    }
    if (from) {
        params.from = moment(from).startOf('day').toISOString();
    }
    if (to) {
        params.to = moment(to).endOf('day').toISOString();
    }
    if (payment_request_id !== '' && payment_request_id != null) {
        const n = parseInt(String(payment_request_id), 10);
        if (!Number.isNaN(n) && n > 0) {
            params.payment_request_id = String(n);
        }
    }
    return params;
};

const buildLogListParams = ({
    search,
    from,
    to,
    delivery_http_status,
    http_method,
    url_contains,
    remote_ip,
    event_status,
    payment_request_id,
}) => {
    const params = { kind: 'rest_api' };
    if (search && search.trim()) {
        params.search = search.trim();
    }
    if (from) {
        params.from = moment(from).startOf('day').toISOString();
    }
    if (to) {
        params.to = moment(to).endOf('day').toISOString();
    }
    if (delivery_http_status) {
        const n = parseInt(delivery_http_status, 10);
        if (!Number.isNaN(n) && n >= 100 && n < 600) {
            params.delivery_http_status = String(n);
        }
    }
    if (http_method && http_method.trim()) {
        params.http_method = http_method.trim().toUpperCase();
    }
    if (url_contains && url_contains.trim()) {
        params.url_contains = url_contains.trim();
    }
    if (remote_ip && remote_ip.trim()) {
        params.remote_ip = remote_ip.trim();
    }
    if (event_status) {
        params.status = event_status;
    }
    if (payment_request_id !== '' && payment_request_id != null) {
        const n = parseInt(String(payment_request_id), 10);
        if (!Number.isNaN(n) && n > 0) {
            params.payment_request_id = String(n);
        }
    }
    return params;
};

const HTTP_STATUS_OPTIONS = [
    { value: '', label: 'Tous codes' },
    { value: '200', label: '200' },
    { value: '201', label: '201' },
    { value: '204', label: '204' },
    { value: '301', label: '301' },
    { value: '302', label: '302' },
    { value: '400', label: '400' },
    { value: '401', label: '401' },
    { value: '403', label: '403' },
    { value: '404', label: '404' },
    { value: '409', label: '409' },
    { value: '422', label: '422' },
    { value: '429', label: '429' },
    { value: '500', label: '500' },
    { value: '502', label: '502' },
    { value: '503', label: '503' },
];

const HTTP_METHOD_OPTIONS = [
    { value: '', label: 'Toutes méthodes' },
    { value: 'GET', label: 'GET' },
    { value: 'POST', label: 'POST' },
    { value: 'PUT', label: 'PUT' },
    { value: 'PATCH', label: 'PATCH' },
    { value: 'DELETE', label: 'DELETE' },
    { value: 'HEAD', label: 'HEAD' },
    { value: 'OPTIONS', label: 'OPTIONS' },
];

const Workbench = () => {
    const dispatch = useDispatch();
    const {
        integration_events = [],
        integration_event,
        isLoading,
        isLoadingOne,
        isReplaying,
        replayError,
    } = useSelector((s) => s.integration_events);
    const { transaction } = useSelector((s) => s.transactions);
    const user = useSelector((s) => s.auth.user);

    const [tab, setTab] = useState('events');
    const [selectedId, setSelectedId] = useState(null);

    const [searchInput, setSearchInput] = useState('');
    const [resourceRefInput, setResourceRefInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [debouncedResourceRef, setDebouncedResourceRef] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [kindFilter, setKindFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [transactionModalId, setTransactionModalId] = useState(null);

    const [logSearchInput, setLogSearchInput] = useState('');
    const [debouncedLogSearch, setDebouncedLogSearch] = useState('');
    const [logDateFrom, setLogDateFrom] = useState('');
    const [logDateTo, setLogDateTo] = useState('');
    const [logHttpStatus, setLogHttpStatus] = useState('');
    const [logHttpMethod, setLogHttpMethod] = useState('');
    const [logUrlInput, setLogUrlInput] = useState('');
    const [debouncedLogUrl, setDebouncedLogUrl] = useState('');
    const [logIpInput, setLogIpInput] = useState('');
    const [debouncedLogIp, setDebouncedLogIp] = useState('');
    const [logEventStatus, setLogEventStatus] = useState('');
    const [paymentRequestFilter, setPaymentRequestFilter] = useState('');
    const [listTxById, setListTxById] = useState({});
    const listTxFetchedOrPendingRef = useRef(new Set());

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchInput), 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedResourceRef(resourceRefInput), 400);
        return () => clearTimeout(t);
    }, [resourceRefInput]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedLogSearch(logSearchInput), 400);
        return () => clearTimeout(t);
    }, [logSearchInput]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedLogUrl(logUrlInput), 400);
        return () => clearTimeout(t);
    }, [logUrlInput]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedLogIp(logIpInput), 400);
        return () => clearTimeout(t);
    }, [logIpInput]);

    const eventListParams = useMemo(
        () =>
            buildEventListParams({
                search: debouncedSearch,
                resource_ref: debouncedResourceRef,
                from: dateFrom,
                to: dateTo,
                kind: kindFilter,
                status: statusFilter,
                payment_request_id: paymentRequestFilter,
            }),
        [
            debouncedSearch,
            debouncedResourceRef,
            dateFrom,
            dateTo,
            kindFilter,
            statusFilter,
            paymentRequestFilter,
        ],
    );

    const logListParams = useMemo(
        () =>
            buildLogListParams({
                search: debouncedLogSearch,
                from: logDateFrom,
                to: logDateTo,
                delivery_http_status: logHttpStatus,
                http_method: logHttpMethod,
                url_contains: debouncedLogUrl,
                remote_ip: debouncedLogIp,
                event_status: logEventStatus,
                payment_request_id: paymentRequestFilter,
            }),
        [
            debouncedLogSearch,
            logDateFrom,
            logDateTo,
            logHttpStatus,
            logHttpMethod,
            debouncedLogUrl,
            debouncedLogIp,
            logEventStatus,
            paymentRequestFilter,
        ],
    );

    useEffect(() => {
        if (!user || !user.isSuperAdmin()) {
            return;
        }
        if (tab !== 'events') {
            return;
        }
        dispatch(loadIntegrationEvents(eventListParams));
    }, [dispatch, user, tab, eventListParams]);

    useEffect(() => {
        if (!user || !user.isSuperAdmin()) {
            return;
        }
        if (tab !== 'logs') {
            return;
        }
        dispatch(loadIntegrationEvents(logListParams));
    }, [dispatch, user, tab, logListParams]);

    useEffect(() => {
        if (!user || !user.isSuperAdmin()) {
            return;
        }
        if (selectedId) {
            dispatch(loadIntegrationEvent(selectedId));
        }
    }, [dispatch, selectedId, user]);

    useEffect(() => {
        if (!user || !user.isSuperAdmin()) {
            return;
        }
        if (!integration_event?.transaction_id) {
            return;
        }
        dispatch(loadTransaction(integration_event.transaction_id));
    }, [dispatch, user, integration_event?.transaction_id]);

    useEffect(() => {
        if (!selectedId || !Array.isArray(integration_events)) {
            return;
        }
        if (!integration_events.some((e) => e.id === selectedId)) {
            setSelectedId(null);
        }
    }, [integration_events, selectedId]);

    const filtered = useMemo(() => {
        if (!Array.isArray(integration_events)) {
            return [];
        }
        if (tab === 'logs') {
            return integration_events.filter((e) => e.kind === 'rest_api');
        }
        return integration_events.filter((e) => e.kind !== 'rest_api');
    }, [integration_events, tab]);

    useEffect(() => {
        if (!user || !user.isSuperAdmin()) {
            return;
        }
        const ids = [...new Set(filtered.map((e) => e.transaction_id).filter(Boolean))];
        ids.forEach((id) => {
            if (transaction?.id === id) {
                return;
            }
            if (listTxFetchedOrPendingRef.current.has(id)) {
                return;
            }
            listTxFetchedOrPendingRef.current.add(id);
            Transactions.get(id)
                .then(({ transaction: t }) => {
                    setListTxById((prev) => ({ ...prev, [id]: t }));
                })
                .catch(() => {
                    listTxFetchedOrPendingRef.current.delete(id);
                });
        });
    }, [filtered, user, transaction?.id]);

    const transactionForListRow = (ev) => {
        if (!ev.transaction_id) {
            return null;
        }
        if (transaction?.id === ev.transaction_id) {
            return transaction;
        }
        return listTxById[ev.transaction_id] || null;
    };

    const reloadList = () => {
        if (tab === 'logs') {
            dispatch(loadIntegrationEvents(logListParams));
        } else {
            dispatch(loadIntegrationEvents(eventListParams));
        }
    };

    const onReplay = (delivery, type) => {
        if (!delivery?.id) {
            return;
        }
        if (type === 'webhook') {
            dispatch(replayWebhook(delivery.id));
        } else {
            dispatch(replayCallback(delivery.id));
        }
        reloadList();
        if (selectedId) {
            dispatch(loadIntegrationEvent(selectedId));
        }
    };

    const resetEventFilters = () => {
        setSearchInput('');
        setResourceRefInput('');
        setDebouncedSearch('');
        setDebouncedResourceRef('');
        setDateFrom('');
        setDateTo('');
        setKindFilter('');
        setStatusFilter('');
        setPaymentRequestFilter('');
    };

    const resetLogFilters = () => {
        setLogSearchInput('');
        setDebouncedLogSearch('');
        setLogDateFrom('');
        setLogDateTo('');
        setLogHttpStatus('');
        setLogHttpMethod('');
        setLogUrlInput('');
        setDebouncedLogUrl('');
        setLogIpInput('');
        setDebouncedLogIp('');
        setLogEventStatus('');
        setPaymentRequestFilter('');
    };

    const canReplay = user && user.isSuperAdmin();

    if (!user || !user.isSuperAdmin()) {
        return <Redirect to="/" />;
    }

    return (
        <div className="Workbench">
            <h1 className="mb-3">Workbench</h1>

            <Nav tabs className="Workbench__tabs mb-3">
                <NavItem>
                    <NavLink
                        className={tab === 'events' ? 'active' : ''}
                        onClick={() => {
                            setTab('events');
                            setSelectedId(null);
                        }}
                        href="#"
                    >
                        Événements
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink
                        className={tab === 'logs' ? 'active' : ''}
                        onClick={() => {
                            setTab('logs');
                            setSelectedId(null);
                        }}
                        href="#"
                    >
                        Logs API
                    </NavLink>
                </NavItem>
            </Nav>

            {tab === 'events' && (
                <div className="Workbench__filters mb-3">
                    <div className="Workbench__filters-row">
                        <InputGroup className="Workbench__filters-search input-group-sm">
                            <InputGroupAddon addonType="prepend">
                                <span className="input-group-text Workbench__filters-search-icon">
                                    <Icon path={mdiMagnify} size={0.85} />
                                </span>
                            </InputGroupAddon>
                            <Input
                                type="search"
                                bsSize="sm"
                                placeholder="Rechercher (nom, canal, réf., order_id, UUID…)"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                aria-label="Recherche (texte et transaction)"
                            />
                        </InputGroup>
                        <div className="Workbench__filters-dates">
                            <Input
                                type="date"
                                bsSize="sm"
                                className="Workbench__filters-date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                                aria-label="Date de début"
                            />
                            <span className="Workbench__filters-date-sep text-muted">→</span>
                            <Input
                                type="date"
                                bsSize="sm"
                                className="Workbench__filters-date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                                aria-label="Date de fin"
                            />
                        </div>
                        <Input
                            type="select"
                            bsSize="sm"
                            className="Workbench__filters-select"
                            value={kindFilter}
                            onChange={(e) => setKindFilter(e.target.value)}
                            aria-label="Type d'événement"
                        >
                            <option value="">Tous les types</option>
                            <option value="psp_webhook">Webhook PSP</option>
                            <option value="merchant_callback">Callback marchand</option>
                            <option value="request_form">Formulaire paiement</option>
                        </Input>
                        <Input
                            type="select"
                            bsSize="sm"
                            className="Workbench__filters-select"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            aria-label="État"
                        >
                            <option value="">Tous les états</option>
                            <option value="success">Succès</option>
                            <option value="failed">Échec</option>
                            <option value="ignored">Ignoré</option>
                        </Input>
                        <Input
                            type="text"
                            bsSize="sm"
                            className="Workbench__filters-resource"
                            placeholder="Ressource (réf.)"
                            value={resourceRefInput}
                            onChange={(e) => setResourceRefInput(e.target.value)}
                            aria-label="Filtre ressource"
                        />
                        {paymentRequestFilter ? (
                            <Badge color="info" className="align-self-center text-nowrap">
                                Demande #{paymentRequestFilter}
                            </Badge>
                        ) : null}
                        <Button
                            type="button"
                            size="sm"
                            color="link"
                            className="Workbench__filters-reset text-muted"
                            onClick={resetEventFilters}
                        >
                            Réinitialiser
                        </Button>
                    </div>
                </div>
            )}

            {tab === 'logs' && (
                <div className="Workbench__filters mb-3">
                    <div className="Workbench__filters-row">
                        <InputGroup className="Workbench__filters-search input-group-sm">
                            <InputGroupAddon addonType="prepend">
                                <span className="input-group-text Workbench__filters-search-icon">
                                    <Icon path={mdiMagnify} size={0.85} />
                                </span>
                            </InputGroupAddon>
                            <Input
                                type="search"
                                bsSize="sm"
                                placeholder="Recherche (URL, contrôleur, order_id, UUID…)"
                                value={logSearchInput}
                                onChange={(e) => setLogSearchInput(e.target.value)}
                                aria-label="Recherche logs et transaction"
                            />
                        </InputGroup>
                        <div className="Workbench__filters-dates">
                            <Input
                                type="date"
                                bsSize="sm"
                                className="Workbench__filters-date"
                                value={logDateFrom}
                                onChange={(e) => setLogDateFrom(e.target.value)}
                                aria-label="Date début logs"
                            />
                            <span className="Workbench__filters-date-sep text-muted">→</span>
                            <Input
                                type="date"
                                bsSize="sm"
                                className="Workbench__filters-date"
                                value={logDateTo}
                                onChange={(e) => setLogDateTo(e.target.value)}
                                aria-label="Date fin logs"
                            />
                        </div>
                        <Input
                            type="select"
                            bsSize="sm"
                            className="Workbench__filters-select Workbench__filters-select--narrow"
                            value={logHttpStatus}
                            onChange={(e) => setLogHttpStatus(e.target.value)}
                            aria-label="Code HTTP réponse"
                        >
                            {HTTP_STATUS_OPTIONS.map((o) => (
                                <option key={o.value || 'all'} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </Input>
                        <Input
                            type="select"
                            bsSize="sm"
                            className="Workbench__filters-select Workbench__filters-select--method"
                            value={logHttpMethod}
                            onChange={(e) => setLogHttpMethod(e.target.value)}
                            aria-label="Méthode HTTP"
                        >
                            {HTTP_METHOD_OPTIONS.map((o) => (
                                <option key={o.value || 'all'} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </Input>
                        <Input
                            type="text"
                            bsSize="sm"
                            className="Workbench__filters-endpoint"
                            placeholder="Endpoint (fragment URL)"
                            value={logUrlInput}
                            onChange={(e) => setLogUrlInput(e.target.value)}
                            aria-label="Filtre endpoint"
                        />
                        <Input
                            type="text"
                            bsSize="sm"
                            className="Workbench__filters-ip"
                            placeholder="Adresse IP"
                            value={logIpInput}
                            onChange={(e) => setLogIpInput(e.target.value)}
                            aria-label="Filtre IP"
                        />
                        <Input
                            type="select"
                            bsSize="sm"
                            className="Workbench__filters-select"
                            value={logEventStatus}
                            onChange={(e) => setLogEventStatus(e.target.value)}
                            aria-label="État de la trace"
                        >
                            <option value="">Trace OK / erreur (tous)</option>
                            <option value="success">Trace réussie</option>
                            <option value="failed">Trace en erreur</option>
                            <option value="ignored">Ignoré</option>
                        </Input>
                        {paymentRequestFilter ? (
                            <Badge color="info" className="align-self-center text-nowrap">
                                Demande #{paymentRequestFilter}
                            </Badge>
                        ) : null}
                        <Button
                            type="button"
                            size="sm"
                            color="link"
                            className="Workbench__filters-reset text-muted"
                            onClick={resetLogFilters}
                        >
                            Réinitialiser
                        </Button>
                    </div>
                </div>
            )}

            {replayError && (
                <div className="alert alert-danger" role="alert">
                    {replayError}
                </div>
            )}

            <Row>
                <Col md={5} className="Workbench__list">
                    <Card>
                        <CardBody>
                            {isLoading && <p>Chargement…</p>}
                            {!isLoading && filtered.length === 0 && <p>Aucun enregistrement.</p>}
                            <ul className="list-unstyled mb-0">
                                {filtered.map((ev) => {
                                    const rowTxLabel =
                                        ev.transaction_id &&
                                        transactionDisplayLabel(
                                            transactionForListRow(ev),
                                            ev.transaction_id,
                                        );
                                    return (
                                    <li
                                        key={ev.id}
                                        className={`Workbench__item border-bottom text-start d-flex align-items-start ${
                                            selectedId === ev.id ? 'Workbench__item--active' : ''
                                        }`}
                                    >
                                        <button
                                            type="button"
                                            className="btn btn-link text-start justify-content-start p-2 flex-grow-1 min-w-0"
                                            onClick={() => setSelectedId(ev.id)}
                                            title={
                                                ev.kind === 'psp_webhook' && ev.name
                                                    ? [ev.channel, ev.name].filter(Boolean).join(': ')
                                                    : undefined
                                            }
                                        >
                                            <div className="fw-bold text-monospace small">
                                                {integrationEventPrimaryTitle(ev)}
                                            </div>
                                            <div className="small text-muted">
                                                {formatIntegrationEventDateTime(ev.occurred_at)}
                                                {ev.user_display_name
                                                    ? ` · ${ev.user_display_name}`
                                                    : ''}
                                            </div>
                                            <div>
                                                <Badge color="info">{integrationEventKindLabel(ev.kind)}</Badge>{' '}
                                                <Badge color={integrationEventStatusBadgeColor(ev.status)}>
                                                    {integrationEventStatusLabel(ev.status)}
                                                </Badge>
                                            </div>
                                        </button>
                                        {(ev.transaction_id || ev.payment_request_id) && (
                                            <div className="Workbench__item-links px-2 pb-2 pt-2 small text-end flex-shrink-1 min-w-0">
                                                {ev.transaction_id && rowTxLabel && (
                                                    <div>
                                                        <Button
                                                            color="link"
                                                            size="sm"
                                                            className="p-0 text-end Workbench__item-tx-label"
                                                            title={rowTxLabel}
                                                            onClick={() => setTransactionModalId(ev.transaction_id)}
                                                        >
                                                            {rowTxLabel}
                                                        </Button>
                                                    </div>
                                                )}
                                                {ev.payment_request_id ? (
                                                    <div className="text-muted">
                                                        Demande n°{ev.payment_request_id}
                                                    </div>
                                                ) : null}
                                            </div>
                                        )}
                                    </li>
                                    );
                                })}
                            </ul>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={7}>
                    <Card>
                        <CardBody>
                            {isLoadingOne && <p>Chargement du détail…</p>}
                            {!selectedId && <p>Sélectionnez un élément.</p>}
                            {integration_event && selectedId === integration_event.id && (() => {
                                const urlParsed =
                                    integration_event.kind === 'merchant_callback' ||
                                    integration_event.kind === 'rest_api' ||
                                    integration_event.kind === 'request_form'
                                        ? parseMerchantCallbackName(integration_event.name)
                                        : null;
                                const detailTitle =
                                    urlParsed && urlParsed.method
                                        ? `${urlParsed.method} ${urlParsed.baseUrl}`
                                        : integrationEventPrimaryTitle(integration_event);
                                const txForLabel =
                                    transaction?.id === integration_event.transaction_id
                                        ? transaction
                                        : null;
                                const txLabel = transactionDisplayLabel(
                                    txForLabel,
                                    integration_event.transaction_id,
                                );
                                return (
                                <>
                                    <h5
                                        className="text-monospace"
                                        title={
                                            integration_event.kind === 'psp_webhook' &&
                                            integration_event.name
                                                ? [
                                                      integration_event.channel,
                                                      integration_event.name,
                                                  ]
                                                      .filter(Boolean)
                                                      .join(': ')
                                                : undefined
                                        }
                                    >
                                        {detailTitle}
                                    </h5>
                                    <p className="small text-muted">
                                        {formatIntegrationEventDateTime(integration_event.occurred_at)}
                                        {integration_event.kind === 'psp_webhook' &&
                                        integration_event.name ? (
                                            <>
                                                {' · '}
                                                <code className="small text-break">
                                                    {integration_event.name}
                                                </code>
                                            </>
                                        ) : (
                                            <>
                                                {' · '}
                                                {integration_event.channel}
                                            </>
                                        )}{' '}
                                        <Badge
                                            color={integrationEventStatusBadgeColor(integration_event.status)}
                                            className="align-middle ms-1"
                                        >
                                            {integrationEventStatusLabel(integration_event.status)}
                                        </Badge>
                                    </p>
                                    {integration_event.user_display_name && (
                                        <p className="small text-muted mb-2">
                                            Utilisateur : {integration_event.user_display_name}
                                        </p>
                                    )}
                                    {(integration_event.transaction_id ||
                                        integration_event.payment_request_id) && (
                                        <div className="mb-3 small border rounded p-2 Workbench__linked">
                                            <div className="font-weight-bold mb-1 Workbench__linked-title">
                                                Lié à
                                            </div>
                                            {integration_event.transaction_id && (
                                                <div className="mb-1 d-flex flex-wrap align-items-center">
                                                    <span className="text-muted mr-2">Transaction</span>
                                                    <code className="Workbench__code small text-break mr-2">
                                                        {txLabel}
                                                    </code>
                                                    <Button
                                                        size="sm"
                                                        color="primary"
                                                        className="Workbench__linked-btn"
                                                        onClick={() =>
                                                            setTransactionModalId(integration_event.transaction_id)
                                                        }
                                                    >
                                                        Afficher
                                                    </Button>
                                                </div>
                                            )}
                                            {integration_event.payment_request_id ? (
                                                <div className="d-flex flex-wrap align-items-center">
                                                    <span className="text-muted mr-2">Demande (paiement)</span>
                                                    <span className="font-weight-bold mr-2">
                                                        #{integration_event.payment_request_id}
                                                    </span>
                                                    <Button
                                                        size="sm"
                                                        color="secondary"
                                                        className="Workbench__linked-btn"
                                                        onClick={() => {
                                                            setPaymentRequestFilter(
                                                                String(integration_event.payment_request_id),
                                                            );
                                                            setTab('events');
                                                            setSelectedId(null);
                                                        }}
                                                    >
                                                        Filtrer sur cette demande
                                                    </Button>
                                                </div>
                                            ) : null}
                                        </div>
                                    )}
                                    {urlParsed && urlParsed.queryPairs.length > 0 && (
                                        <>
                                            <h6 className="mt-3">Paramètres d&apos;URL</h6>
                                            <dl className="Workbench__query-dl small mb-3">
                                                {urlParsed.queryPairs.map(({ key, value }, idx) => (
                                                    <div
                                                        className="Workbench__query-row"
                                                        key={`${key}-${idx}`}
                                                    >
                                                        <dt className="Workbench__query-key">{key}</dt>
                                                        <dd className="Workbench__query-val">{value}</dd>
                                                    </div>
                                                ))}
                                            </dl>
                                        </>
                                    )}
                                    <h6 className="mt-3">Payload</h6>
                                    <pre className="Workbench__pre">
                                        {formatJson(integration_event.payload)}
                                    </pre>
                                    <h6 className="mt-3">Livraisons</h6>
                                    {(integration_event.deliveries || []).map((d) => (
                                        <div key={d.id} className="border rounded p-2 mb-2">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <Badge color="info">{d.direction}</Badge>{' '}
                                                    <Badge color="primary">{d.http_method}</Badge>{' '}
                                                    <span className="small">
                                                        {integration_event.kind === 'merchant_callback' ||
                                                        integration_event.kind === 'rest_api' ||
                                                        integration_event.kind === 'request_form'
                                                            ? urlWithoutQuery(d.url)
                                                            : d.url}
                                                    </span>
                                                </div>
                                                <Role has={User.ROLE_ADMIN}>
                                                    {canReplay && tab === 'events' && (
                                                        <>
                                                            {integration_event.kind === 'psp_webhook' &&
                                                                d.direction === 'inbound' && (
                                                                <Button
                                                                    size="sm"
                                                                    color="primary"
                                                                    disabled={isReplaying}
                                                                    onClick={() => onReplay(d, 'webhook')}
                                                                >
                                                                    Renvoyer webhook
                                                                </Button>
                                                            )}
                                                            {integration_event.kind === 'merchant_callback' &&
                                                                d.direction === 'outbound' && (
                                                                <Button
                                                                    size="sm"
                                                                    color="primary"
                                                                    disabled={isReplaying}
                                                                    onClick={() => onReplay(d, 'callback')}
                                                                >
                                                                    Renvoyer callback
                                                                </Button>
                                                            )}
                                                        </>
                                                    )}
                                                </Role>
                                            </div>
                                            <pre className="Workbench__pre small mt-2">
                                                {formatJson({
                                                    http_status: d.http_status,
                                                    duration_ms: d.duration_ms,
                                                    remote_ip: d.remote_ip,
                                                    request_body: d.request_body,
                                                    response_body: d.response_body,
                                                    error_message: d.error_message,
                                                })}
                                            </pre>
                                        </div>
                                    ))}
                                </>
                                );
                            })()}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {transactionModalId && (
                <TransactionModal
                    transaction_id={transactionModalId}
                    show
                    toggle={() => setTransactionModalId(null)}
                    onClose={() => setTransactionModalId(null)}
                />
            )}
        </div>
    );
};

export default Workbench;
